import { FEATURES, WorkflowStatus } from '@makemydeal/dr-sales-checkin-types';
import type { FeatureName } from '@makemydeal/dr-sales-checkin-types';
import { isFeatureVisible } from '../capabilities/selector';
import { bootstrapIdleRule } from './rules/bootstrapIdleRule';
import { bootstrapErrorRule } from './rules/bootstrapErrorRule';
import { roleAssumptionRule } from './rules/roleAssumptionRule';
import { missingDealershipRule } from './rules/missingDealershipRule';
import { standardInvalidRule } from './rules/standardInvalidRule';
import { missingVehicleRule } from './rules/missingVehicleRule';
import { standardValidRule } from './rules/standardValidRule';
import { acceptedRule } from './rules/acceptedRule';
import { deskedRule } from './rules/deskedRule';
import { sentForDeskingRule } from './rules/sentForDeskingRule';
import { defaultRule } from './rules/defaultRule';
import { type GuardedWorkflowRule } from './types';

// Evaluated in priority order — first match wins.
// Lower number = higher priority. defaultRule is the catch-all.
const workflowRules: GuardedWorkflowRule[] = [
    { capability: FEATURES.GLOBAL, rule: bootstrapIdleRule }, // P0: API in flight — show loader, suppress navigation
    { capability: FEATURES.GLOBAL, rule: bootstrapErrorRule }, // P1: bootstrap failed — show error page
    { capability: FEATURES.GLOBAL, rule: roleAssumptionRule }, // P2: role selection required
    { capability: FEATURES.GLOBAL, rule: missingDealershipRule }, // P3: any mode + deal has no dealership → Deal Setup
    { capability: FEATURES.GLOBAL, rule: standardInvalidRule }, // P4: standard mode + invalid deal → Deal Setup
    { capability: FEATURES.GLOBAL, rule: standardValidRule }, // P5: standard mode + valid → redirect to Sales View
    { capability: FEATURES.GLOBAL, rule: missingVehicleRule }, // P6: showroom mode + missing vehicle → inventory
    // P7-P9 are NOT mutually exclusive, so this order is load-bearing. DESKED and ACCEPTED need the
    // `deal360.publishedVersion` marker present; SENT_FOR_DESKING normally needs it absent, but its
    // trade-in arm (an outstanding approval request on a trade-in deal) deliberately survives a live
    // marker and therefore overlaps both. Ranking it last confines that arm to the deals the other
    // two did not claim — and matches the BFF's rule table, so the status the server seeds at
    // bootstrap survives the client's first local re-evaluation.
    { capability: FEATURES.DEAL_SUMMARY, rule: acceptedRule }, // P7: showroom mode + published + accepted
    { capability: FEATURES.DEAL_SUMMARY, rule: deskedRule }, // P8: showroom mode + published + manager approved
    { capability: FEATURES.DEAL_SUMMARY, rule: sentForDeskingRule }, // P9: showroom mode + manager asked, nothing published back
    { capability: FEATURES.GLOBAL, rule: defaultRule } // P10: showroom mode + in progress → vehicle details
];

/**
 * Whether the capability a rule is declared under permits it to run.
 * Every other capability goes through the same fail-closed selector nav, routes and
 * feature hooks use, so a stage whose feature is HIDDEN cannot become the workflow
 * status no matter what the deal signals say.
 */

const isRuleAllowed = (capability: FeatureName, state: any): boolean =>
    capability === FEATURES.GLOBAL || isFeatureVisible(capability)(state);

/**
 * Derives the current WorkflowStatus by evaluating rules in priority order,
 * skipping any rule whose capability is not allowed for the current edition.
 * Returns the status from the first matching rule, or IN_PROGRESS as fallback.
 */
export const evaluate = (state: any): WorkflowStatus => {
    for (const { capability, rule } of workflowRules) {
        if (!isRuleAllowed(capability, state)) continue;

        const status = rule(state);
        if (status !== null) return status;
    }
    // Fallback: defaultRule should always match, but fail-safe to IN_PROGRESS
    return WorkflowStatus.IN_PROGRESS;
};
